/**
 * 업데이트 이력 한 버전의 표시용 모양. `src/generated/changelog.d.ts` 와 같은 구조다
 * (그 파일은 prebuild 생성 JSON 의 계약 문서, 이 파일은 런타임 타입 — JSON 이 없는
 * tsc/테스트에서도 import 경로가 깨지지 않도록 타입을 여기서도 선언한다).
 */
export interface ChangelogEntry {
  version: string;
  date: string;
  sections: Record<string, string[]>;
}

/** 이 순서대로 절을 보여준다. 모르는 절은 뒤에 원래 이름 그대로 붙는다. */
export const SECTION_ORDER: string[] = ['Added', 'Changed', 'Fixed', 'Security', 'Removed'];

function isEntry(value: unknown): value is ChangelogEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<ChangelogEntry>;
  return (
    typeof entry.version === 'string'
    && typeof entry.date === 'string'
    && !!entry.sections
    && typeof entry.sections === 'object'
  );
}

/** 읽기 결과 — 빈 목록이 "이력 없음"인지 "불러오지 못함"인지 구분한다. */
export interface ChangelogLoadResult {
  entries: ChangelogEntry[];
  /** 생성 JSON 을 읽지 못했으면 true. */
  failed: boolean;
}

/**
 * prebuild 가 만든 변경 이력을 읽는다.
 *
 * `src/generated/changelog.json` 은 .gitignore 대상이라 저장소에는 없다. `tsc`/테스트처럼
 * prebuild 없이 도는 상황에서도 앱이 깨지지 않도록 require 를 try/catch 로 감싼다 —
 * 다만 빈 배열로 뭉개지 않고 `failed` 로 알려, 모달이 "이력 없음"과 "불러오지 못함"을
 * 구분해 보여준다(빌드 전에는 prebuild 가 항상 파일을 먼저 만든다).
 */
export function loadChangelog(): ChangelogLoadResult {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const data = require('../../generated/changelog.json');
    if (!Array.isArray(data)) return { entries: [], failed: true };
    return { entries: data.filter(isEntry), failed: false };
  } catch {
    return { entries: [], failed: true };
  }
}

/** 화면 표시용 버전 — `0.34.0` → `v0.34.0`, 이미 v 가 있으면 그대로. */
export function formatVersion(version: string): string {
  const trimmed = (version || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('v') || trimmed.startsWith('V') ? `v${trimmed.slice(1)}` : `v${trimmed}`;
}
